import React, { useEffect, useState } from "react";
import { FaTimes, FaCheck, FaLock, FaPlus, FaTrash, FaChevronDown, FaChevronUp, FaHistory, FaUnlock, FaDollarSign, FaEye, FaClipboardCheck, FaStar, FaFileUpload, FaDrawPolygon, FaFileSignature, FaEdit } from "react-icons/fa";
import axios from 'axios';
import SignatureBoxEditor from '../../components/SignatureBoxEditor';

const ClientMenuModal = ({
  showMenu, setShowMenu, selectedMenuUser, setSelectedMenuUser, selectedStep, setSelectedStep,
  handleFormAssignment, handleMemberFormAssignment, handleStepSubmit, handleMemberStepSubmit,
  isStepSubmitted, isMemberStepSubmitted, canSubmitStep, canSubmitMemberStep,
  toggleServiceLock, toggleLoading, handleMemberNameUpdate, addMember, removeMember,
  handleUSAFormationStepSubmit, isUSAFormationStepSubmitted, canSubmitUSAFormationStep,
  handleCompanyFormAssignment, handleTaxDateUpdate, handleMemberTaxDateUpdate,
  handleAnnualReportServiceUpdate, handleRegisteredAgentDueDateUpdate,
  handleMoveToHistory, handleUnlockTaxFiling, handleUpdateTaxFilingUnlockPrice,
  handleUpdateTaxSummaryTable, handleOpenStep1Checklist,
  handleOpenStep3CustomTable, handleViewStep3Submission, getStep3Status, userSteps,
  handleHistoryTaxDateUpdate
}) => {
  const [activeTab, setActiveTab] = useState("primary");
  const [taxFilingSubTab, setTaxFilingSubTab] = useState("primary-member");
  const [newMemberName, setNewMemberName] = useState("");
  const [activeMemberAccordion, setActiveMemberAccordion] = useState(null);
  const [selectedMemberService, setSelectedMemberService] = useState("tax");
  const [primaryTaxYearEndDate, setPrimaryTaxYearEndDate] = useState("");
  const [primaryDueDate, setPrimaryDueDate] = useState("");
  const [memberTaxYearEndDates, setMemberTaxYearEndDates] = useState({});
  const [memberDueDates, setMemberDueDates] = useState({});

  // Annual Report Service states
  const [annualReportState, setAnnualReportState] = useState("");
  const [annualReportEntityType, setAnnualReportEntityType] = useState("");
  const [annualReportRegistrationDate, setAnnualReportRegistrationDate] = useState("");
  const [showEntityTypeSelect, setShowEntityTypeSelect] = useState(false);

  // Registered Agent Service states
  const [registeredAgentDueDate, setRegisteredAgentDueDate] = useState("");

  // Move to History states
  const [newCycleTaxYearEndDate, setNewCycleTaxYearEndDate] = useState("");
  const [newCycleDueDate, setNewCycleDueDate] = useState("");
  const [showMoveToHistoryConfirm, setShowMoveToHistoryConfirm] = useState(false);
  const [moveToHistoryMemberIndex, setMoveToHistoryMemberIndex] = useState(null);

  // Tax Filing unlock price (USD) - per user
  const [taxUnlockPriceUsd, setTaxUnlockPriceUsd] = useState("150");
  const [isSavingTaxUnlockPrice, setIsSavingTaxUnlockPrice] = useState(false);

  // History editing states
  const [editingHistory, setEditingHistory] = useState(null); // { type, index, historyIndex }
  const [historyEditTaxYearEnd, setHistoryEditTaxYearEnd] = useState("");
  const [historyEditDueDate, setHistoryEditDueDate] = useState("");

  // Step 4 Document Signing states
  const [step4DocUploading, setStep4DocUploading] = useState(false);
  const [step4EditorOpen, setStep4EditorOpen] = useState(false);
  const [step4EditorDocUrl, setStep4EditorDocUrl] = useState('');
  const [step4EditorBoxes, setStep4EditorBoxes] = useState([]);
  const [step4EditorTarget, setStep4EditorTarget] = useState({ userId: null, memberIndex: -1 });
  const [step4SavingBoxes, setStep4SavingBoxes] = useState(false);

  // Disable background scrolling when modal is open
  useEffect(() => {
    if (showMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMenu]);

  // Default pre-populated Tax Filing summary table (matches screenshot)
  const DEFAULT_TAX_SUMMARY_TABLE = {
    columns: ["PARTICULARS", "CURRENT YEAR", "PREVIOUS YEAR"],
    rows: [
      ["Total Income  ", "", ""],
      ["Total Tax ", "", ""]
    ]
  };

  const cloneDefaultTable = () => ({
    columns: [...DEFAULT_TAX_SUMMARY_TABLE.columns],
    rows: DEFAULT_TAX_SUMMARY_TABLE.rows.map(row => [...row])
  });

  // Helpers for editable Tax Filing summary tables
  const getPrimarySummaryTable = () => {
    const t = selectedMenuUser?.taxFilingSummaryTable;
    if (t && Array.isArray(t.columns) && t.columns.length > 0) return t;
    return cloneDefaultTable();
  };

  const handlePrimaryTableChange = (updater) => {
    setSelectedMenuUser(prev => {
      if (!prev) return prev;
      const t = prev.taxFilingSummaryTable;
      const current = (t && Array.isArray(t.columns) && t.columns.length > 0) ? t : cloneDefaultTable();
      const updated = updater(current);
      return { ...prev, taxFilingSummaryTable: updated };
    });
  };

  const getMemberSummaryTable = (member) => {
    const t = member?.taxFilingSummaryTable;
    if (t && Array.isArray(t.columns) && t.columns.length > 0) return t;
    return cloneDefaultTable();
  };

  const handleMemberTableChange = (memberIndex, updater) => {
    setSelectedMenuUser(prev => {
      if (!prev || !prev.members) return prev;
      const members = [...prev.members];
      const currentMember = members[memberIndex];
      if (!currentMember) return prev;

      const t = currentMember.taxFilingSummaryTable;
      const currentTable = (t && Array.isArray(t.columns) && t.columns.length > 0) ? t : DEFAULT_TAX_SUMMARY_TABLE;
      const updatedTable = updater(currentTable);

      members[memberIndex] = {
        ...currentMember,
        taxFilingSummaryTable: updatedTable
      };

      return { ...prev, members };
    });
  };

  useEffect(() => {
    if (!selectedMenuUser) return;
    const cents = selectedMenuUser.taxFilingUnlockPriceCents;
    const usd = (Number.isFinite(cents) && cents >= 100) ? (cents / 100) : 150;
    setTaxUnlockPriceUsd(String(usd));
  }, [selectedMenuUser]);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  // Step 4: Upload signature document handler
  const handleStep4DocUpload = async (userId, memberIndex, file) => {
    if (!file) return;

    // Check Step 3 status before allowing upload
    const isPrimary = memberIndex === -1 || memberIndex === null || memberIndex === undefined;
    const step3Status = getStep3Status ? getStep3Status(userId, isPrimary ? -1 : memberIndex) : 'pending';

    if (step3Status !== 'submitted') {
      alert('Step 4 upload is only possible after Step 3 is completed.');
      return;
    }

    setStep4DocUploading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('document', file);
      formData.append('userId', userId);
      formData.append('memberIndex', memberIndex !== null && memberIndex !== undefined ? memberIndex : -1);
      const res = await axios.post(`${API_BASE}/api/auth/upload-signature-document`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.signatureDocument) {
        // Refresh user data by updating the local state
        const isPrimary = memberIndex === -1 || memberIndex === null || memberIndex === undefined;
        setSelectedMenuUser(prev => {
          if (!prev) return prev;
          const updated = { ...prev };
          if (isPrimary) {
            const steps = [...(updated.steps || [])];
            const s4 = steps.find(s => s.step === 4);
            if (s4) s4.signatureDocument = res.data.signatureDocument;
            updated.steps = steps;
          } else {
            const members = [...(updated.members || [])];
            if (members[memberIndex]) {
              const steps = [...(members[memberIndex].steps || [])];
              const s4 = steps.find(s => s.step === 4);
              if (s4) s4.signatureDocument = res.data.signatureDocument;
              members[memberIndex] = { ...members[memberIndex], steps };
            }
            updated.members = members;
          }
          return updated;
        });
      }
    } catch (err) {
      console.error('Error uploading Step 4 document:', err);
      alert(err.response?.data?.message || 'Failed to upload document');
    } finally {
      setStep4DocUploading(false);
    }
  };

  // Step 4: Open signature box editor
  const openStep4Editor = (userId, memberIndex) => {
    const isPrimary = memberIndex === -1 || memberIndex === null || memberIndex === undefined;
    let sigDoc;
    if (isPrimary) {
      const s4 = selectedMenuUser?.steps?.find(s => s.step === 4);
      sigDoc = s4?.signatureDocument;
    } else {
      const s4 = selectedMenuUser?.members?.[memberIndex]?.steps?.find(s => s.step === 4);
      sigDoc = s4?.signatureDocument;
    }
    if (!sigDoc?.fileUrl) return;
    setStep4EditorDocUrl(`${API_BASE}${sigDoc.fileUrl}`);
    setStep4EditorBoxes(sigDoc.signatureBoxes || []);
    setStep4EditorTarget({ userId, memberIndex: isPrimary ? -1 : memberIndex });
    setStep4EditorOpen(true);
  };

  // Step 4: Save signature boxes
  const handleStep4SaveBoxes = async (boxes) => {
    setStep4SavingBoxes(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${API_BASE}/api/auth/save-signature-boxes`, {
        userId: step4EditorTarget.userId,
        memberIndex: step4EditorTarget.memberIndex,
        signatureBoxes: boxes
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) {
        const isPrimary = step4EditorTarget.memberIndex === -1;
        setSelectedMenuUser(prev => {
          if (!prev) return prev;
          const updated = { ...prev };
          if (isPrimary) {
            const steps = [...(updated.steps || [])];
            const s4 = steps.find(s => s.step === 4);
            if (s4) {
              s4.signatureDocument = { ...s4.signatureDocument, signatureBoxes: boxes };
              s4.status = 'client-pending';
            }
            updated.steps = steps;
          } else {
            const mIdx = step4EditorTarget.memberIndex;
            const members = [...(updated.members || [])];
            if (members[mIdx]) {
              const steps = [...(members[mIdx].steps || [])];
              const s4 = steps.find(s => s.step === 4);
              if (s4) {
                s4.signatureDocument = { ...s4.signatureDocument, signatureBoxes: boxes };
                s4.status = 'client-pending';
              }
              members[mIdx] = { ...members[mIdx], steps };
            }
            updated.members = members;
          }
          return updated;
        });
        setStep4EditorOpen(false);
      }
    } catch (err) {
      console.error('Error saving signature boxes:', err);
      alert(err.response?.data?.message || 'Failed to save signature boxes');
    } finally {
      setStep4SavingBoxes(false);
    }
  };

  // Helper: Get step 4 signature document status
  const getStep4DocStatus = (userId, memberIndex) => {
    const isPrimary = memberIndex === -1 || memberIndex === null || memberIndex === undefined;
    let s4;
    if (isPrimary) {
      s4 = selectedMenuUser?.steps?.find(s => s.step === 4);
    } else {
      s4 = selectedMenuUser?.members?.[memberIndex]?.steps?.find(s => s.step === 4);
    }
    if (!s4?.signatureDocument?.fileUrl) return 'no-document';
    if (s4.signatureDocument.signedFileUrl) return 'signed';
    if (s4.signatureDocument.signatureBoxes?.length > 0) return 'boxes-placed';
    return 'document-uploaded';
  };

  const handleTabSelect = (tab) => {
    setActiveTab(tab);
    setActiveMemberAccordion(null);
    if (tab === 'primary') setTaxFilingSubTab('primary-member');
  };

  const toggleMemberAccordion = (index) => {
    setActiveMemberAccordion(activeMemberAccordion === index ? null : index);
  };

  const handleAddMemberWithService = async () => {
    try {
      const memberIndex = await addMember(selectedMenuUser._id);
      if (newMemberName) {
        await handleMemberNameUpdate(selectedMenuUser._id, memberIndex, newMemberName);
        setNewMemberName("");
      }
    } catch (error) {
      console.error("Error adding member:", error);
    }
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Helper function to format date for input field
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Helper function to get due date period text
  const getDueDatePeriod = (formType) => {
    if (formType === '1040' || formType === '1120') {
      return '3.5 months';
    } else if (formType === '1065' || formType === '1120-S') {
      return '2.5 months';
    }
    return '';
  };

  // Check if all steps are completed (handles both old 5-step and new 4-step structures)
  const areAllStepsCompleted = (user, memberIndex = null) => {
    let steps;
    if (memberIndex !== null && memberIndex >= 0) {
      steps = user?.members?.[memberIndex]?.steps;
    } else {
      steps = user?.steps;
    }
    if (!steps || steps.length === 0) return false;
    // Filter to relevant steps (1-4) and check all are submitted
    const relevantSteps = steps.filter(s => s.step <= 4);
    return relevantSteps.length >= 4 && relevantSteps.every(s => s.status === 'submitted');
  };

  // Handle move to history confirmation
  const confirmMoveToHistory = async () => {
    if (handleMoveToHistory) {
      await handleMoveToHistory(selectedMenuUser._id, moveToHistoryMemberIndex, newCycleTaxYearEndDate, newCycleDueDate);
      setShowMoveToHistoryConfirm(false);
      setNewCycleTaxYearEndDate("");
      setNewCycleDueDate("");
      setMoveToHistoryMemberIndex(null);
    }
  };

  if (!showMenu) return null;

  const tabs = [
    { id: 'services', label: 'Services' },
    { id: 'primary', label: 'Tax Filing' },
    { id: 'activity-report', label: 'Activity Report' },
    { id: 'usa-formation', label: 'USA Company Formation' },
    { id: 'annual-report', label: 'Annual Report' },
    { id: 'registered-agent', label: 'Registered Agent' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex" onClick={() => setShowMenu(false)}>
      {/* Backdrop with fade-in */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300" />

      {/* Right Sidebar Panel */}
      <div
        className="ml-auto relative flex h-full w-full max-w-[820px] shadow-2xl animate-slideInRight"
        onClick={e => e.stopPropagation()}
        style={{ animation: 'slideInRight 0.3s ease-out forwards' }}
      >
        {/* Vertical Tab Navigation */}
        <div className="w-16 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 flex flex-col items-center py-4 gap-1 shrink-0">
          {/* Close Button at Top */}
          <button
            onClick={() => setShowMenu(false)}
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-all mb-4"
          >
            <FaTimes size={14} />
          </button>

          {/* Tab Icons */}
          {tabs.map(tab => {
            const tabIcons = {
              'services': <FaLock size={14} />,
              'primary': <FaClipboardCheck size={14} />,
              'activity-report': <FaHistory size={14} />,
              'usa-formation': <FaStar size={14} />,
              'annual-report': <FaFileUpload size={14} />,
              'registered-agent': <FaCheck size={14} />
            };
            return (
              <button
                key={tab.id}
                onClick={() => handleTabSelect(tab.id)}
                className={`w-12 h-12 flex flex-col items-center justify-center rounded-xl transition-all duration-200 group relative ${activeTab === tab.id
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/30'
                  : 'text-gray-500 hover:text-white hover:bg-white/10'
                  }`}
                title={tab.label}
              >
                {tabIcons[tab.id]}
                <span className="text-[8px] mt-0.5 font-medium leading-tight text-center truncate w-full px-0.5">
                  {tab.label.split(' ')[0]}
                </span>
                {/* Tooltip on hover */}
                <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-10 shadow-lg">
                  {tab.label}
                  <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col bg-white dark:bg-dark-800 overflow-hidden">
          {/* Header with Client Info */}
          <div className="px-6 py-4 border-b border-gray-100 dark:border-dark-700 bg-gradient-to-r from-gray-50 to-white dark:from-dark-800 dark:to-dark-750 shrink-0">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-primary-500/20 shrink-0">
                {selectedMenuUser?.name?.charAt(0)?.toUpperCase() || 'C'}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                  {selectedMenuUser?.name || 'Client'}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {selectedMenuUser?.email || 'No email'}
                </p>
              </div>
              {/* Active Tab Badge */}
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 border border-primary-200 dark:border-primary-800 shrink-0">
                {tabs.find(t => t.id === activeTab)?.label}
              </span>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {/* Services Tab */}
            {activeTab === 'services' && (
              <div>
                <h5 className="font-bold text-gray-900 dark:text-white mb-4">Service Lock/Unlock</h5>
                <div className="space-y-2 bg-gray-50 dark:bg-dark-700 p-4 rounded-xl">
                  {[
                    { id: "usa-company-switch", label: "USA Company Formation", key: "isUSAEnabled" },
                    { id: "tax-filing-switch", label: "Tax Filing", key: "isTaxFilingEnabled" },
                    { id: "bookkeeping-switch", label: "Bookkeeping", key: "isBookkeepingEnabled" },
                    { id: "cfo-switch", label: "CFO Advisory", key: "isCFOEnabled" },
                    { id: "registered-agent-switch", label: "Registered Agent", key: "isRegisteredAgentEnabled" },
                    { id: "annual-report-switch", label: "Annual Report Service", key: "isAnnualReportServiceEnabled" }
                  ].map(service => {
                    const toggleKey = `${selectedMenuUser?._id}-${service.label}`;
                    const isLoading = toggleLoading?.[toggleKey] || false;

                    return (
                      <div key={service.id} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-dark-600 last:border-0">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{service.label}</span>
                        <label className={`relative inline-flex items-center ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={selectedMenuUser?.[service.key] || false}
                            onChange={(e) => toggleServiceLock(selectedMenuUser._id, service.label, e.target.checked)}
                            className="sr-only peer"
                            disabled={isLoading}
                          />
                          <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-dark-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600 transition-all duration-200 ${isLoading ? 'opacity-60' : ''}`}></div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tax Filing Tab */}
            {activeTab === 'primary' && (
              <div className="space-y-6">
                {/* Sub-tabs: Primary Member | Member */}
                <div className="flex border-b border-gray-200 dark:border-dark-600">
                  <button
                    onClick={() => setTaxFilingSubTab('primary-member')}
                    className={`px-5 py-2.5 text-sm font-semibold transition-all ${taxFilingSubTab === 'primary-member'
                      ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50 dark:bg-primary-900/10'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                      }`}
                  >
                    Primary Member
                  </button>
                  <button
                    onClick={() => setTaxFilingSubTab('member')}
                    className={`px-5 py-2.5 text-sm font-semibold transition-all ${taxFilingSubTab === 'member'
                      ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50 dark:bg-primary-900/10'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                      }`}
                  >
                    Member {selectedMenuUser?.members?.length > 0 ? `(${selectedMenuUser.members.length})` : ''}
                  </button>
                </div>

                {/* Primary Member Sub-tab Content */}
                {taxFilingSubTab === 'primary-member' && (
                  <>
                    <div className="grid gap-6 lg:grid-cols-2">
                      <div>
                        <h5 className="font-bold text-gray-900 dark:text-white mb-3">Assign Form</h5>
                        <div className="flex gap-2">
                          <select value={selectedMenuUser?.assignedForm || "1040"} onChange={(e) => setSelectedMenuUser({ ...selectedMenuUser, assignedForm: e.target.value })} className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white">
                            <option value="1040">1040</option>
                            <option value="1065">1065</option>
                            <option value="1120-S">1120-S</option>
                            <option value="1120">1120</option>
                          </select>
                          <button onClick={() => handleFormAssignment(selectedMenuUser._id, selectedMenuUser.assignedForm)} className="px-4 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">Assign</button>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-bold text-gray-900 dark:text-white mb-3">Tax Year End Date & Due Date</h5>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div>
                            <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-1">Tax Year End</label>
                            <input type="date" value={primaryTaxYearEndDate || formatDateForInput(selectedMenuUser?.taxYearEndDate)} onChange={(e) => setPrimaryTaxYearEndDate(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-1">Due Date (Optional)</label>
                            <input type="date" value={primaryDueDate || formatDateForInput(selectedMenuUser?.dueDate)} onChange={(e) => setPrimaryDueDate(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white" />
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const tyed = primaryTaxYearEndDate || formatDateForInput(selectedMenuUser?.taxYearEndDate);
                            const dd = primaryDueDate || formatDateForInput(selectedMenuUser?.dueDate);
                            if (tyed) {
                              handleTaxDateUpdate(selectedMenuUser._id, tyed, dd);
                              setPrimaryTaxYearEndDate("");
                              setPrimaryDueDate("");
                            }
                          }}
                          disabled={!primaryTaxYearEndDate && !selectedMenuUser?.taxYearEndDate}
                          className="w-full px-4 py-2.5 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50"
                        >
                          Update Tax Dates
                        </button>
                        {(selectedMenuUser?.taxYearEndDate || selectedMenuUser?.dueDate) && (
                          <button
                            onClick={() => {
                              handleTaxDateUpdate(selectedMenuUser._id, null, null, true);
                              setPrimaryTaxYearEndDate("");
                              setPrimaryDueDate("");
                            }}
                            className="w-full mt-2 px-4 py-2.5 bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-100"
                          >
                            Remove Dates
                          </button>
                        )}
                        {selectedMenuUser?.taxYearEndDate && selectedMenuUser?.dueDate && (
                          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-xl text-sm">
                            <strong>Current Tax Year End:</strong> {formatDateForDisplay(selectedMenuUser.taxYearEndDate)}<br />
                            <strong>Due Date:</strong> {formatDateForDisplay(selectedMenuUser.dueDate)}
                            {getDueDatePeriod(selectedMenuUser.assignedForm) && <span className="text-primary-600"> ({getDueDatePeriod(selectedMenuUser.assignedForm)})</span>}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid gap-6 lg:grid-cols-2">
                      {selectedMenuUser?.assignedForm && (
                        <div>
                          <h5 className="font-bold text-gray-900 dark:text-white mb-3">Step 1: Initial Information (Checklist)</h5>
                          <button
                            onClick={() => handleOpenStep1Checklist && handleOpenStep1Checklist(selectedMenuUser, -1)}
                            className="w-full px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 flex items-center justify-center gap-2"
                          >
                            <FaEye /> Preview / Edit Step 1 Checklist
                          </button>
                          <p className="mt-2 p-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg text-sm">
                            Admin can view and save the checklist.
                          </p>
                        </div>
                      )}
                      <div>
                        <h5 className="font-bold text-gray-900 dark:text-white mb-3">Submit Primary Steps</h5>
                        <select value={selectedStep} onChange={(e) => setSelectedStep(parseInt(e.target.value))} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white mb-2">
                          {[2, 3, 4].map(step => {
                            const submitted = isStepSubmitted(selectedMenuUser, step);
                            const canSub = canSubmitStep(selectedMenuUser, step);
                            return <option key={step} value={step} disabled={submitted || !canSub}>Step {step}{submitted && " ✓"}{!submitted && !canSub && " (Locked)"}</option>;
                          })}
                        </select>
                        <button onClick={handleStepSubmit} disabled={isStepSubmitted(selectedMenuUser, selectedStep) || !canSubmitStep(selectedMenuUser, selectedStep)} className="w-full px-4 py-2.5 bg-amber-500 text-white font-medium rounded-xl hover:bg-amber-600 disabled:opacity-50">{isStepSubmitted(selectedMenuUser, selectedStep) ? "Already Submitted" : "Submit Step"}</button>
                        {isStepSubmitted(selectedMenuUser, selectedStep) && <p className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-sm">This step has already been submitted.</p>}
                        {!canSubmitStep(selectedMenuUser, selectedStep) && !isStepSubmitted(selectedMenuUser, selectedStep) && <p className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 rounded-lg text-sm">Complete previous steps first.</p>}
                      </div>
                    </div>

                    {/* Step 3 Management */}
                    {selectedMenuUser?.assignedForm && getStep3Status && (
                      <div className="mt-6">
                        <h5 className="font-bold text-gray-900 dark:text-white mb-3">Step 3: Draft Tax Return Management</h5>
                        <div className="grid gap-4 lg:grid-cols-2">
                          <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-xl">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                              Current Status: <span className={`font-semibold ${getStep3Status(selectedMenuUser._id, -1) === 'client-pending' ? 'text-orange-600' : getStep3Status(selectedMenuUser._id, -1) === 'submitted' ? 'text-green-600' : 'text-blue-600'}`}>
                                {getStep3Status(selectedMenuUser._id, -1) === 'client-pending' ? 'Client Pending' : getStep3Status(selectedMenuUser._id, -1) === 'submitted' ? 'Completed' : getStep3Status(selectedMenuUser._id, -1) === 'in-progress' ? 'In Progress' : 'Pending'}
                              </span>
                            </p>
                            {getStep3Status(selectedMenuUser._id, -1) === 'in-progress' && (
                              <button
                                onClick={() => handleOpenStep3CustomTable && handleOpenStep3CustomTable(selectedMenuUser, -1)}
                                className="w-full px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700"
                              >
                                Create Custom Table & Set Client Pending
                              </button>
                            )}
                            {(getStep3Status(selectedMenuUser._id, -1) === 'client-pending' || getStep3Status(selectedMenuUser._id, -1) === 'submitted') && (
                              <button
                                onClick={() => handleViewStep3Submission && handleViewStep3Submission(selectedMenuUser, -1)}
                                className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
                              >
                                View Client Submission
                              </button>
                            )}
                          </div>
                          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                            <p className="text-sm text-blue-800 dark:text-blue-300">
                              <strong>Step 3 Workflow:</strong><br />
                              1. After Step 2 completes, Step 3 auto-sets to "In Progress"<br />
                              2. Create custom table and set to "Client Pending"<br />
                              3. Client fills and submits the table<br />
                              4. Review submission and mark as "Completed"
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 4: Document Upload & Signature Boxes (Primary) - Only shown when Step 3 completed and Step 4 NOT yet submitted */}
                    {selectedMenuUser?.assignedForm &&
                      getStep3Status?.(selectedMenuUser._id, -1) === 'submitted' &&
                      !isStepSubmitted(selectedMenuUser, 4) && (
                        <div className="mt-6">
                          <h5 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <FaFileSignature className="text-purple-500" /> Step 4: Document Upload & Signature Boxes
                          </h5>
                          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl space-y-3">
                            {/* Upload Document */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Upload PDF Document for Signing
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="file"
                                  accept=".pdf"
                                  onChange={(e) => {
                                    if (e.target.files[0]) handleStep4DocUpload(selectedMenuUser._id, -1, e.target.files[0]);
                                    e.target.value = '';
                                  }}
                                  disabled={step4DocUploading}
                                  className="flex-1 px-3 py-2 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-purple-600 file:text-white file:text-xs file:font-medium file:cursor-pointer"
                                />
                              </div>
                              {step4DocUploading && <p className="text-xs text-purple-600 mt-1 animate-pulse">Uploading document...</p>}
                            </div>

                            {/* Status & Actions */}
                            {(() => {
                              const status = getStep4DocStatus(selectedMenuUser._id, -1);
                              const s4 = selectedMenuUser?.steps?.find(s => s.step === 4);
                              return (
                                <>
                                  {status === 'no-document' && (
                                    <p className="text-xs text-gray-500">No document uploaded yet.</p>
                                  )}
                                  {status === 'document-uploaded' && (
                                    <div className="flex items-center justify-between bg-white dark:bg-dark-700 p-3 rounded-lg">
                                      <div>
                                        <p className="text-sm font-medium text-gray-800 dark:text-white">📄 {s4?.signatureDocument?.originalName}</p>
                                        <p className="text-xs text-blue-600">Document uploaded. Add signature boxes next.</p>
                                      </div>
                                      <button
                                        onClick={() => openStep4Editor(selectedMenuUser._id, -1)}
                                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700"
                                      >
                                        <FaDrawPolygon /> Add Signature Boxes
                                      </button>
                                    </div>
                                  )}
                                  {status === 'boxes-placed' && (
                                    <div className="flex items-center justify-between bg-white dark:bg-dark-700 p-3 rounded-lg">
                                      <div>
                                        <p className="text-sm font-medium text-gray-800 dark:text-white">📄 {s4?.signatureDocument?.originalName}</p>
                                        <p className="text-xs text-orange-600">✅ {s4?.signatureDocument?.signatureBoxes?.length} signature box(es) placed. Waiting for client to sign.</p>
                                      </div>
                                      <button
                                        onClick={() => openStep4Editor(selectedMenuUser._id, -1)}
                                        className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700"
                                      >
                                        <FaEye /> Edit Boxes
                                      </button>
                                    </div>
                                  )}
                                  {status === 'signed' && (
                                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                      <p className="text-sm font-medium text-green-700 dark:text-green-400">✅ Document signed by client</p>
                                      <p className="text-xs text-green-600">Signed at: {s4?.signatureDocument?.signedAt ? new Date(s4.signatureDocument.signedAt).toLocaleString() : 'N/A'}</p>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                    {/* Primary Member Summary Table (Tax Filing) */}
                    <div>
                      <h5 className="font-bold text-gray-900 dark:text-white mb-3">Primary Member Tax Filing Table</h5>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Configure a custom summary table for the primary member&apos;s Tax Filing. This table will be visible to the user only after all four Tax Filing steps are completed.
                      </p>
                      <div className="border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden">
                        <div className="p-3 bg-gray-50 dark:bg-dark-700 flex flex-wrap items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handlePrimaryTableChange(table => {
                                const columns = [...(table.columns || [])];
                                const rows = (table.rows || []).map(r => [...r]);
                                const newColumnName = `Column ${columns.length + 1}`;
                                columns.push(newColumnName);
                                const nextRows = rows.map(r => [...r, ""]);
                                return { ...table, columns, rows: nextRows };
                              })
                            }
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                          >
                            + Add Column
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handlePrimaryTableChange(table => {
                                const columns = table.columns || [];
                                const rows = [...(table.rows || [])];
                                const newRow = columns.map(() => "");
                                rows.push(newRow);
                                return { ...table, rows };
                              })
                            }
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700"
                          >
                            + Add Row
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handlePrimaryTableChange(() => cloneDefaultTable())
                            }
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40"
                          >
                            Reset Table
                          </button>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="min-w-full border-t border-gray-200 dark:border-dark-600 text-xs">
                            <thead className="bg-gray-50 dark:bg-dark-700">
                              <tr>
                                {(getPrimarySummaryTable().columns || []).map((col, colIndex) => (
                                  <th key={colIndex} className="px-2 py-2 border-b border-gray-200 dark:border-dark-600">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        value={col}
                                        onChange={e =>
                                          handlePrimaryTableChange(table => {
                                            const columns = [...(table.columns || [])];
                                            columns[colIndex] = e.target.value;
                                            return { ...table, columns };
                                          })
                                        }
                                        className="w-full px-2 py-1 rounded border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white font-semibold"
                                        placeholder={`Column ${colIndex + 1}`}
                                      />
                                      {(getPrimarySummaryTable().columns?.length || 0) > 1 && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handlePrimaryTableChange(table => {
                                              const columns = (table.columns || []).filter((_, index) => index !== colIndex);
                                              const rows = (table.rows || []).map(row => row.filter((_, index) => index !== colIndex));
                                              return { ...table, columns, rows };
                                            })
                                          }
                                          className="text-red-600 hover:text-red-800"
                                          aria-label="Delete Column"
                                        >
                                          ×
                                        </button>
                                      )}
                                    </div>
                                  </th>
                                ))}
                                {((getPrimarySummaryTable().columns?.length || 0) > 0) && (
                                  <th className="px-2 py-2 border-b border-gray-200 dark:border-dark-600 text-right text-gray-500 dark:text-gray-400 text-[10px]">
                                    Actions
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {(getPrimarySummaryTable().rows || []).map((row, rowIndex) => (
                                <tr key={rowIndex} className="odd:bg-white even:bg-gray-50 dark:odd:bg-dark-800 dark:even:bg-dark-700/70">
                                  {(getPrimarySummaryTable().columns || []).map((_, colIndex) => (
                                    <td key={colIndex} className="px-2 py-1.5 border-b border-gray-100 dark:border-dark-700">
                                      <input
                                        type="text"
                                        value={row[colIndex] ?? ""}
                                        onChange={e =>
                                          handlePrimaryTableChange(table => {
                                            const rows = (table.rows || []).map(r => [...r]);
                                            if (!rows[rowIndex]) {
                                              rows[rowIndex] = (table.columns || []).map(() => "");
                                            }
                                            rows[rowIndex][colIndex] = e.target.value;
                                            return { ...table, rows };
                                          })
                                        }
                                        className="w-full px-2 py-1 rounded border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-900 text-gray-900 dark:text-white"
                                      />
                                    </td>
                                  ))}
                                  {getPrimarySummaryTable().columns?.length > 0 && (
                                    <td className="px-2 py-1.5 border-b border-gray-100 dark:border-dark-700 text-right">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handlePrimaryTableChange(table => {
                                            const rows = (table.rows || []).filter((_, idx) => idx !== rowIndex);
                                            return { ...table, rows };
                                          })
                                        }
                                        className="inline-flex items-center justify-center px-2 py-1 text-[10px] font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                                      >
                                        Delete Row
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {handleUpdateTaxSummaryTable && (
                          <div className="p-3 border-t border-gray-200 dark:border-dark-600 flex justify-end">
                            <button
                              type="button"
                              onClick={async () => {
                                if (!selectedMenuUser?._id) return;
                                await handleUpdateTaxSummaryTable(
                                  selectedMenuUser._id,
                                  null,
                                  getPrimarySummaryTable()
                                );
                              }}
                              className="px-4 py-2 text-xs font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                            >
                              Save Table
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tax Filing Lock Status */}
                    <div>
                      <h5 className="font-bold text-gray-900 dark:text-white mb-3">Tax Filing Lock Status</h5>
                      <div className={`p-4 rounded-xl ${selectedMenuUser?.taxFilingLocked ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800' : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {selectedMenuUser?.taxFilingLocked ? (
                              <><FaLock className="text-orange-500" /><span className="font-medium text-orange-700 dark:text-orange-400">Tax Filing is Locked</span></>
                            ) : (
                              <><FaUnlock className="text-green-500" /><span className="font-medium text-green-700 dark:text-green-400">Tax Filing is Unlocked</span></>
                            )}
                          </div>
                          {selectedMenuUser?.taxFilingLocked && handleUnlockTaxFiling && (
                            <button onClick={() => handleUnlockTaxFiling(selectedMenuUser._id, null)} className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 text-sm">
                              Unlock (Admin)
                            </button>
                          )}
                        </div>
                      </div>

                    </div>

                    {/* Move to History Section */}
                    {areAllStepsCompleted(selectedMenuUser) && !selectedMenuUser?.taxFilingLocked && (
                      <div>
                        <h5 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <FaHistory className="text-primary-500" /> Move to History & Start New Cycle
                        </h5>
                        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl">
                          <p className="text-sm text-purple-700 dark:text-purple-400 mb-3">
                            All 4 steps are completed. You can move this tax filing cycle to history and start a new locked cycle.
                          </p>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">New Tax Year End Date</label>
                              <input type="date" value={newCycleTaxYearEndDate} onChange={(e) => setNewCycleTaxYearEndDate(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">New Due Date</label>
                              <input type="date" value={newCycleDueDate} onChange={(e) => setNewCycleDueDate(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm" />
                            </div>
                          </div>
                          <button onClick={() => { setMoveToHistoryMemberIndex(null); setShowMoveToHistoryConfirm(true); }} className="w-full px-4 py-2.5 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 flex items-center justify-center gap-2">
                            <FaHistory /> Move to History & Lock New Cycle
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Tax Filing History */}
                    {selectedMenuUser?.taxFilingHistory?.length > 0 && (
                      <div>
                        <h5 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <FaHistory className="text-gray-500" /> Tax Filing History ({selectedMenuUser.taxFilingHistory.length})
                        </h5>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {selectedMenuUser.taxFilingHistory.map((history, idx) => (
                            <div key={idx} className="p-3 bg-gray-50 dark:bg-dark-700 rounded-lg text-sm">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-900 dark:text-white">Form {history.assignedForm}</span>
                                <div className="flex items-center gap-2">
                                  {editingHistory?.type === 'primary' && editingHistory?.historyIndex === idx ? (
                                    <>
                                      <button
                                        onClick={() => {
                                          handleHistoryTaxDateUpdate(selectedMenuUser._id, null, idx, historyEditTaxYearEnd, historyEditDueDate);
                                          setEditingHistory(null);
                                        }}
                                        className="text-green-600 hover:text-green-700 text-xs font-bold"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => setEditingHistory(null)}
                                        className="text-gray-400 hover:text-gray-600 text-xs"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setEditingHistory({ type: 'primary', historyIndex: idx });
                                        setHistoryEditTaxYearEnd(formatDateForInput(history.taxYearEndDate));
                                        setHistoryEditDueDate(formatDateForInput(history.dueDate));
                                      }}
                                      className="text-primary-600 hover:text-primary-700 text-xs flex items-center gap-1"
                                    >
                                      <FaEdit /> Edit
                                    </button>
                                  )}
                                  <span className="text-green-600 dark:text-green-400 text-xs">✓ Completed</span>
                                </div>
                              </div>
                              {editingHistory?.type === 'primary' && editingHistory?.historyIndex === idx ? (
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  <div>
                                    <label className="block text-[10px] text-gray-500">Tax Year End</label>
                                    <input
                                      type="date"
                                      value={historyEditTaxYearEnd}
                                      onChange={(e) => setHistoryEditTaxYearEnd(e.target.value)}
                                      className="w-full px-2 py-1 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded text-xs"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] text-gray-500">Due Date</label>
                                    <input
                                      type="date"
                                      value={historyEditDueDate}
                                      onChange={(e) => setHistoryEditDueDate(e.target.value)}
                                      className="w-full px-2 py-1 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded text-xs"
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Tax Year: {formatDateForDisplay(history.taxYearEndDate)} | Due: {formatDateForDisplay(history.dueDate)} | Completed: {formatDateForDisplay(history.completedAt)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* User Feedback */}
                    <div>
                      <h5 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <FaStar className="text-yellow-400" /> User Feedback
                      </h5>
                      {selectedMenuUser?.feedback?.rating ? (
                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex items-center gap-0.5">
                              {[1, 2, 3, 4, 5].map(star => (
                                <FaStar
                                  key={star}
                                  className={`w-4 h-4 ${star <= selectedMenuUser.feedback.rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
                                />
                              ))}
                            </div>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {selectedMenuUser.feedback.rating}/5
                            </span>
                          </div>
                          {selectedMenuUser.feedback.comment && (
                            <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                              &ldquo;{selectedMenuUser.feedback.comment}&rdquo;
                            </p>
                          )}
                          {selectedMenuUser.feedback.submittedAt && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                              Submitted: {formatDateForDisplay(selectedMenuUser.feedback.submittedAt)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="p-4 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl">
                          <p className="text-sm text-gray-500 dark:text-gray-400">No feedback submitted yet.</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Member Sub-tab Content */}
                {taxFilingSubTab === 'member' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:border-r md:pr-6 border-gray-200 dark:border-dark-700">
                      <h5 className="font-bold text-gray-900 dark:text-white mb-3">Add New Member</h5>
                      <select value={selectedMemberService} onChange={(e) => setSelectedMemberService(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white mb-3">
                        <option value="usa">USA Company Formation</option>
                        <option value="tax">Tax Filing</option>
                        <option value="bookkeeping">Bookkeeping</option>
                        <option value="cfo">CFO Advisory</option>
                      </select>
                      <div className="flex gap-2 mb-4">
                        <input type="text" placeholder="New Member Name" value={newMemberName} onChange={(e) => setNewMemberName(e.target.value)} className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white" />
                        <button onClick={handleAddMemberWithService} disabled={selectedMenuUser?.members?.length >= 50} className="px-4 py-2.5 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50"><FaPlus /></button>
                      </div>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {selectedMenuUser?.members?.map((member, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-dark-700 rounded-xl">
                            <input type="text" value={member.name || ""} placeholder={`Member ${index + 1}`} onChange={(e) => { const u = [...selectedMenuUser.members]; u[index].name = e.target.value; setSelectedMenuUser({ ...selectedMenuUser, members: u }); }} className="flex-1 px-3 py-2 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm" />
                            <button onClick={() => { handleMemberNameUpdate(selectedMenuUser._id, index, member.name); toggleMemberAccordion(index); }} className="px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700">Save</button>
                            <button onClick={() => removeMember(selectedMenuUser._id, index)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"><FaTrash /></button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="font-bold text-gray-900 dark:text-white mb-3">Member Details</h5>
                      <div className="space-y-2">
                        {selectedMenuUser?.members?.map((member, index) => (
                          <div key={index} className="border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden">
                            <button onClick={() => toggleMemberAccordion(index)} className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600">
                              <div className="flex items-center gap-3">
                                <span className="font-medium text-gray-900 dark:text-white">Member {index + 1}: {member.name || "Unnamed"}</span>
                                {member?.feedback?.rating && (
                                  <div className="flex items-center gap-0.5 ml-2">
                                    {[1, 2, 3, 4, 5].map(star => (
                                      <FaStar key={star} size={10} className={`${star <= member.feedback.rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`} />
                                    ))}
                                  </div>
                                )}
                              </div>
                              {activeMemberAccordion === index ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                            </button>
                            {activeMemberAccordion === index && (
                              <div className="p-4 space-y-4 bg-white dark:bg-dark-800">
                                <div>
                                  <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">Assign Form</h6>
                                  <div className="flex gap-2">
                                    <select value={member.assignedForm || "1040"} onChange={(e) => { const u = [...selectedMenuUser.members]; u[index].assignedForm = e.target.value; setSelectedMenuUser({ ...selectedMenuUser, members: u }); }} className="flex-1 px-3 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm">
                                      <option value="1040">1040</option>
                                      <option value="1065">1065</option>
                                      <option value="1120-S">1120-S</option>
                                      <option value="1120">1120</option>
                                    </select>
                                    <button onClick={() => handleMemberFormAssignment(selectedMenuUser._id, index, member.assignedForm)} className="px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700">Assign</button>
                                  </div>
                                </div>

                                <div>
                                  <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">Tax Year End Date & Due Date</h6>
                                  <div className="grid grid-cols-2 gap-2 mb-2">
                                    <div>
                                      <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-1">Tax Year End</label>
                                      <input type="date" value={memberTaxYearEndDates[index] || formatDateForInput(member?.taxYearEndDate)} onChange={(e) => setMemberTaxYearEndDates({ ...memberTaxYearEndDates, [index]: e.target.value })} className="w-full px-3 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm" />
                                    </div>
                                    <div>
                                      <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-1">Due Date (Opt)</label>
                                      <input type="date" value={memberDueDates[index] || formatDateForInput(member?.dueDate)} onChange={(e) => setMemberDueDates({ ...memberDueDates, [index]: e.target.value })} className="w-full px-3 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm" />
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => {
                                      const tyed = memberTaxYearEndDates[index] || formatDateForInput(member?.taxYearEndDate);
                                      const dd = memberDueDates[index] || formatDateForInput(member?.dueDate);
                                      if (tyed) {
                                        handleMemberTaxDateUpdate(selectedMenuUser._id, index, tyed, dd);
                                        setMemberTaxYearEndDates({ ...memberTaxYearEndDates, [index]: "" });
                                        setMemberDueDates({ ...memberDueDates, [index]: "" });
                                      }
                                    }}
                                    disabled={!memberTaxYearEndDates[index] && !member?.taxYearEndDate}
                                    className="w-full px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                                  >
                                    Update Member Dates
                                  </button>
                                  {(member?.taxYearEndDate || member?.dueDate) && (
                                    <button
                                      onClick={() => {
                                        handleMemberTaxDateUpdate(selectedMenuUser._id, index, null, null, true);
                                        setMemberTaxYearEndDates({ ...memberTaxYearEndDates, [index]: "" });
                                        setMemberDueDates({ ...memberDueDates, [index]: "" });
                                      }}
                                      className="w-full mt-2 px-3 py-2 bg-red-50 text-red-600 text-sm font-medium rounded-lg hover:bg-red-100"
                                    >
                                      Remove Dates
                                    </button>
                                  )}
                                  {member?.taxYearEndDate && member?.dueDate && (
                                    <p className="mt-2 text-xs p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg"><strong>Tax Year End:</strong> {formatDateForDisplay(member.taxYearEndDate)} | <strong>Due:</strong> {formatDateForDisplay(member.dueDate)}</p>
                                  )}
                                </div>

                                {member.assignedForm && (
                                  <div>
                                    <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">Step 1: Initial Information (Checklist)</h6>
                                    <button onClick={() => handleOpenStep1Checklist && handleOpenStep1Checklist(selectedMenuUser, index)} className="w-full px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2"><FaEye /> Preview / Edit Step 1 Checklist</button>
                                    <p className="mt-1 text-[11px] text-indigo-600 dark:text-indigo-400">Admin can view and save. Only user can Confirm & Submit.</p>
                                  </div>
                                )}

                                <div>
                                  <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">Submit Steps</h6>
                                  <select value={selectedStep} onChange={(e) => setSelectedStep(parseInt(e.target.value))} className="w-full px-3 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm mb-2">
                                    {[2, 3, 4].map(step => {
                                      const sub = isMemberStepSubmitted(selectedMenuUser, index, step);
                                      const can = canSubmitMemberStep(selectedMenuUser, index, step);
                                      return <option key={step} value={step} disabled={sub || !can}>Step {step}{sub && " ✓"}{!sub && !can && " (Locked)"}</option>;
                                    })}
                                  </select>
                                  <button onClick={() => handleMemberStepSubmit(index)} disabled={isMemberStepSubmitted(selectedMenuUser, index, selectedStep) || !canSubmitMemberStep(selectedMenuUser, index, selectedStep)} className="w-full px-3 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50">{isMemberStepSubmitted(selectedMenuUser, index, selectedStep) ? "Already Submitted" : "Submit Step"}</button>
                                </div>

                                {member.assignedForm && getStep3Status && (
                                  <div>
                                    <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">Step 3: Draft Tax Return Management</h6>
                                    <div className="p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                                      <p className="text-[11px] text-gray-600 dark:text-gray-400 mb-2">
                                        Current Status: <span className={`font-semibold ${getStep3Status(selectedMenuUser._id, index) === 'client-pending' ? 'text-orange-600' : getStep3Status(selectedMenuUser._id, index) === 'submitted' ? 'text-green-600' : 'text-blue-600'}`}>
                                          {getStep3Status(selectedMenuUser._id, index) === 'client-pending' ? 'Client Pending' : getStep3Status(selectedMenuUser._id, index) === 'submitted' ? 'Completed' : getStep3Status(selectedMenuUser._id, index) === 'in-progress' ? 'In Progress' : 'Pending'}
                                        </span>
                                      </p>
                                      {getStep3Status(selectedMenuUser._id, index) === 'in-progress' && (
                                        <button onClick={() => handleOpenStep3CustomTable && handleOpenStep3CustomTable(selectedMenuUser, index)} className="w-full px-3 py-2 bg-orange-600 text-white text-xs font-medium rounded-lg hover:bg-orange-700">Create Table & Set Client Pending</button>
                                      )}
                                      {(getStep3Status(selectedMenuUser._id, index) === 'client-pending' || getStep3Status(selectedMenuUser._id, index) === 'submitted') && (
                                        <button onClick={() => handleViewStep3Submission && handleViewStep3Submission(selectedMenuUser, index)} className="w-full px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">View Client Submission</button>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {member.assignedForm && getStep3Status?.(selectedMenuUser._id, index) === 'submitted' && !isMemberStepSubmitted(selectedMenuUser, index, 4) && (
                                  <div>
                                    <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm flex items-center gap-2"><FaFileSignature className="text-purple-500" /> Step 4: Document & Signature</h6>
                                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg space-y-2">
                                      <input type="file" accept=".pdf" onChange={(e) => { if (e.target.files[0]) handleStep4DocUpload(selectedMenuUser._id, index, e.target.files[0]); e.target.value = ''; }} disabled={step4DocUploading} className="w-full px-2 py-1.5 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-purple-600 file:text-white file:text-[10px] file:font-medium file:cursor-pointer" />
                                      {step4DocUploading && <p className="text-[10px] text-purple-600 animate-pulse">Uploading...</p>}
                                      {(() => {
                                        const status = getStep4DocStatus(selectedMenuUser._id, index);
                                        const s4 = member?.steps?.find(s => s.step === 4);
                                        return (
                                          <>
                                            {status === 'document-uploaded' && (
                                              <div className="flex items-center justify-between bg-white dark:bg-dark-700 p-2 rounded-lg">
                                                <p className="text-xs text-blue-600 truncate flex-1">📄 {s4?.signatureDocument?.originalName}</p>
                                                <button onClick={() => openStep4Editor(selectedMenuUser._id, index)} className="flex items-center gap-1 px-2 py-1 bg-purple-600 text-white text-[10px] font-medium rounded hover:bg-purple-700 ml-2"><FaDrawPolygon size={10} /> Add Boxes</button>
                                              </div>
                                            )}
                                            {status === 'boxes-placed' && (
                                              <div className="flex items-center justify-between bg-white dark:bg-dark-700 p-2 rounded-lg">
                                                <div className="flex-1"><p className="text-xs text-orange-600">✅ {s4?.signatureDocument?.signatureBoxes?.length} box(es) — Client Pending</p></div>
                                                <button onClick={() => openStep4Editor(selectedMenuUser._id, index)} className="flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white text-[10px] font-medium rounded hover:bg-indigo-700 ml-2"><FaEye size={10} /> Edit</button>
                                              </div>
                                            )}
                                            {status === 'signed' && (<p className="text-xs text-green-600 font-medium">✅ Signed by client</p>)}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                )}

                                {/* Member Tax Filing Summary Table */}
                                <div>
                                  <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">Member Tax Filing Table</h6>
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">Configure a custom Tax Filing table for this member.</p>
                                  <div className="border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden">
                                    <div className="p-2 bg-gray-50 dark:bg-dark-700 flex flex-wrap gap-2 items-center">
                                      <button type="button" onClick={() => handleMemberTableChange(index, table => { const columns = [...(table.columns || [])]; const rows = (table.rows || []).map(r => [...r]); columns.push(`Column ${columns.length + 1}`); const nextRows = rows.map(r => [...r, ""]); return { ...table, columns, rows: nextRows }; })} className="px-2 py-1 text-[11px] font-medium rounded bg-primary-600 text-white hover:bg-primary-700">+ Add Column</button>
                                      <button type="button" onClick={() => handleMemberTableChange(index, table => { const columns = table.columns || []; const rows = [...(table.rows || [])]; rows.push(columns.map(() => "")); return { ...table, rows }; })} className="px-2 py-1 text-[11px] font-medium rounded bg-green-600 text-white hover:bg-green-700">+ Add Row</button>
                                      <button type="button" onClick={() => handleMemberTableChange(index, () => cloneDefaultTable())} className="px-2 py-1 text-[11px] font-medium rounded bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40">Reset Table</button>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full border-t border-gray-200 dark:border-dark-600 text-[11px]">
                                        <thead className="bg-gray-50 dark:bg-dark-700">
                                          <tr>
                                            {(getMemberSummaryTable(member).columns || []).map((col, colIndex) => (
                                              <th key={colIndex} className="px-2 py-1.5 border-b border-gray-200 dark:border-dark-600">
                                                <div className="flex items-center gap-2">
                                                  <input type="text" value={col} onChange={e => handleMemberTableChange(index, table => { const columns = [...(table.columns || [])]; columns[colIndex] = e.target.value; return { ...table, columns }; })} className="w-full px-2 py-1 rounded border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white font-semibold" placeholder={`Column ${colIndex + 1}`} />
                                                  {(getMemberSummaryTable(member).columns?.length || 0) > 1 && (
                                                    <button type="button" onClick={() => handleMemberTableChange(index, table => { const columns = (table.columns || []).filter((_, idx) => idx !== colIndex); const rows = (table.rows || []).map(row => row.filter((_, idx) => idx !== colIndex)); return { ...table, columns, rows }; })} className="text-red-600 hover:text-red-800" aria-label="Delete Column">×</button>
                                                  )}
                                                </div>
                                              </th>
                                            ))}
                                            {((getMemberSummaryTable(member).columns?.length || 0) > 0) && (<th className="px-2 py-1.5 border-b border-gray-200 dark:border-dark-600 text-right text-gray-500 dark:text-gray-400 text-[10px]">Actions</th>)}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(getMemberSummaryTable(member).rows || []).map((row, rowIndex) => (
                                            <tr key={rowIndex} className="odd:bg-white even:bg-gray-50 dark:odd:bg-dark-800 dark:even:bg-dark-700/70">
                                              {(getMemberSummaryTable(member).columns || []).map((_, colIndex) => (
                                                <td key={colIndex} className="px-2 py-1.5 border-b border-gray-100 dark:border-dark-700">
                                                  <input type="text" value={row[colIndex] ?? ""} onChange={e => handleMemberTableChange(index, table => { const rows = (table.rows || []).map(r => [...r]); if (!rows[rowIndex]) { rows[rowIndex] = (table.columns || []).map(() => ""); } rows[rowIndex][colIndex] = e.target.value; return { ...table, rows }; })} className="w-full px-2 py-1 rounded border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-900 text-gray-900 dark:text-white" />
                                                </td>
                                              ))}
                                              {getMemberSummaryTable(member).columns?.length > 0 && (
                                                <td className="px-2 py-1.5 border-b border-gray-100 dark:border-dark-700 text-right">
                                                  <button type="button" onClick={() => handleMemberTableChange(index, table => { const rows = (table.rows || []).filter((_, idx) => idx !== rowIndex); return { ...table, rows }; })} className="inline-flex items-center justify-center px-2 py-1 text-[10px] font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">Delete Row</button>
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    {handleUpdateTaxSummaryTable && (
                                      <div className="p-2 border-t border-gray-200 dark:border-dark-600 flex justify-end">
                                        <button type="button" onClick={async () => { if (!selectedMenuUser?._id) return; await handleUpdateTaxSummaryTable(selectedMenuUser._id, index, getMemberSummaryTable(member)); }} className="px-3 py-1.5 text-[11px] font-semibold rounded bg-primary-600 text-white hover:bg-primary-700">Save Member Table</button>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {areAllStepsCompleted(selectedMenuUser, index) && !member?.taxFilingLocked && (
                                  <div>
                                    <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm flex items-center gap-2"><FaHistory className="text-purple-500" /> Move to History & Start New Cycle</h6>
                                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl">
                                      <p className="text-xs text-purple-700 dark:text-purple-400 mb-2">All 4 steps are completed for {member.name || `Member ${index + 1}`}.</p>
                                      <div className="grid grid-cols-2 gap-2 mb-2">
                                        <div><label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-1">New Tax Year End</label><input type="date" value={newCycleTaxYearEndDate} onChange={(e) => setNewCycleTaxYearEndDate(e.target.value)} className="w-full px-2 py-1.5 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-xs" /></div>
                                        <div><label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-1">New Due Date</label><input type="date" value={newCycleDueDate} onChange={(e) => setNewCycleDueDate(e.target.value)} className="w-full px-2 py-1.5 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-xs" /></div>
                                      </div>
                                      <button onClick={() => { setMoveToHistoryMemberIndex(index); setShowMoveToHistoryConfirm(true); }} className="w-full px-3 py-2 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"><FaHistory /> Move to History & Lock</button>
                                    </div>
                                  </div>
                                )}

                                {member?.taxFilingLocked && (
                                  <div>
                                    <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm flex items-center gap-2"><FaLock className="text-orange-500" /> Tax Filing Locked</h6>
                                    <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl flex items-center justify-between">
                                      <p className="text-xs text-orange-700 dark:text-orange-400">New cycle is locked. User must pay to unlock.</p>
                                      <button onClick={() => handleUnlockTaxFiling && handleUnlockTaxFiling(selectedMenuUser._id, index)} className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 flex items-center gap-1"><FaUnlock /> Admin Unlock</button>
                                    </div>
                                  </div>
                                )}

                                {member?.taxFilingHistory?.length > 0 && (
                                  <div>
                                    <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm flex items-center gap-2"><FaHistory className="text-gray-500" /> Tax Filing History ({member.taxFilingHistory.length})</h6>
                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                      {member.taxFilingHistory.map((history, hIdx) => (
                                        <div key={hIdx} className="p-2 bg-gray-50 dark:bg-dark-700 rounded-lg text-xs">
                                          <div className="flex items-center justify-between">
                                            <span className="font-medium text-gray-900 dark:text-white">Form {history.assignedForm}</span>
                                            <div className="flex items-center gap-2">
                                              {editingHistory?.type === 'member' && editingHistory?.index === index && editingHistory?.historyIndex === hIdx ? (
                                                <>
                                                  <button
                                                    onClick={() => {
                                                      handleHistoryTaxDateUpdate(selectedMenuUser._id, index, hIdx, historyEditTaxYearEnd, historyEditDueDate);
                                                      setEditingHistory(null);
                                                    }}
                                                    className="text-green-600 hover:text-green-700 text-[10px] font-bold"
                                                  >
                                                    Save
                                                  </button>
                                                  <button
                                                    onClick={() => setEditingHistory(null)}
                                                    className="text-gray-400 hover:text-gray-600 text-[10px]"
                                                  >
                                                    Cancel
                                                  </button>
                                                </>
                                              ) : (
                                                <button
                                                  onClick={() => {
                                                    setEditingHistory({ type: 'member', index: index, historyIndex: hIdx });
                                                    setHistoryEditTaxYearEnd(formatDateForInput(history.taxYearEndDate));
                                                    setHistoryEditDueDate(formatDateForInput(history.dueDate));
                                                  }}
                                                  className="text-primary-600 hover:text-primary-700 text-[10px] flex items-center gap-1"
                                                >
                                                  <FaEdit /> Edit
                                                </button>
                                              )}
                                              <span className="text-green-600 dark:text-green-400 text-[10px]">✓ Completed</span>
                                            </div>
                                          </div>
                                          {editingHistory?.type === 'member' && editingHistory?.index === index && editingHistory?.historyIndex === hIdx ? (
                                            <div className="grid grid-cols-2 gap-2 mt-1">
                                              <div>
                                                <label className="block text-[8px] text-gray-500">Tax Year End</label>
                                                <input
                                                  type="date"
                                                  value={historyEditTaxYearEnd}
                                                  onChange={(e) => setHistoryEditTaxYearEnd(e.target.value)}
                                                  className="w-full px-1.5 py-0.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded text-[10px]"
                                                />
                                              </div>
                                              <div>
                                                <label className="block text-[8px] text-gray-500">Due Date</label>
                                                <input
                                                  type="date"
                                                  value={historyEditDueDate}
                                                  onChange={(e) => setHistoryEditDueDate(e.target.value)}
                                                  className="w-full px-1.5 py-0.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded text-[10px]"
                                                />
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                                              Tax Year: {formatDateForDisplay(history.taxYearEndDate)} | Due: {formatDateForDisplay(history.dueDate)} | Completed: {formatDateForDisplay(history.completedAt)}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Member Feedback */}
                                <div>
                                  <h6 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm flex items-center gap-2"><FaStar className="text-yellow-400" /> Member Feedback</h6>
                                  {member?.feedback?.rating ? (
                                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className="flex items-center gap-0.5">{[1, 2, 3, 4, 5].map(star => (<FaStar key={star} className={`w-3 h-3 ${star <= member.feedback.rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`} />))}</div>
                                        <span className="text-xs font-semibold text-gray-900 dark:text-white">{member.feedback.rating}/5</span>
                                      </div>
                                      {member.feedback.comment && (<p className="text-xs text-gray-700 dark:text-gray-300 italic">&ldquo;{member.feedback.comment}&rdquo;</p>)}
                                      {member.feedback.submittedAt && (<p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">Submitted: {formatDateForDisplay(member.feedback.submittedAt)}</p>)}
                                    </div>
                                  ) : (
                                    <div className="p-3 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl"><p className="text-xs text-gray-500 dark:text-gray-400 text-center">No feedback yet.</p></div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Activity Report Tab */}
            {activeTab === 'activity-report' && (
              <div className="space-y-6">
                <h5 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FaHistory className="text-primary-500" /> Tax Filing Activity Report
                </h5>

                {/* Helper function to format date with time and seconds */}
                {(() => {
                  const formatDetailedDateTime = (dateString) => {
                    if (!dateString) return <span className="text-gray-400 italic">Not yet recorded</span>;
                    const date = new Date(dateString);
                    return (
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {date.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </div>
                    );
                  };

                  return (
                    <>
                      {/* Primary Member Activity */}
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-5 border border-blue-200 dark:border-blue-800">
                        <h6 className="font-bold text-lg text-blue-900 dark:text-blue-300 mb-4 flex items-center gap-2">
                          <FaClipboardCheck /> Primary Member - {selectedMenuUser?.name}
                        </h6>

                        <div className="space-y-4">
                          {/* Tax Filing Enabled Date */}
                          <div className="bg-white dark:bg-dark-800 rounded-lg p-4 shadow-sm">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                  📅 Tax Filing Enabled Date
                                </h6>
                                {formatDetailedDateTime(selectedMenuUser?.taxFilingEnabledAt)}
                              </div>
                            </div>
                          </div>

                          {/* Step Completion Dates */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[1, 2, 3, 4].map(stepNum => (
                              <div key={stepNum} className="bg-white dark:bg-dark-800 rounded-lg p-4 shadow-sm">
                                <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                  ✅ Step {stepNum} Completion
                                </h6>
                                {formatDetailedDateTime(selectedMenuUser?.[`step${stepNum}CompletionDate`])}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Members Activity */}
                      {selectedMenuUser?.members && selectedMenuUser.members.length > 0 && (
                        <div className="space-y-4">
                          <h6 className="font-bold text-lg text-gray-900 dark:text-white">Members Activity</h6>
                          {selectedMenuUser.members.map((member, index) => (
                            <div key={index} className="bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 rounded-xl p-5 border border-green-200 dark:border-green-800">
                              <h6 className="font-bold text-lg text-green-900 dark:text-green-300 mb-4 flex items-center gap-2">
                                <FaClipboardCheck /> Member {index + 1} - {member.name || 'Unnamed'}
                              </h6>

                              <div className="space-y-4">
                                {/* Tax Filing Enabled Date */}
                                <div className="bg-white dark:bg-dark-800 rounded-lg p-4 shadow-sm">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                        📅 Tax Filing Enabled Date
                                      </h6>
                                      {formatDetailedDateTime(member.taxFilingEnabledAt)}
                                    </div>
                                  </div>
                                </div>

                                {/* Step Completion Dates */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {[1, 2, 3, 4].map(stepNum => (
                                    <div key={stepNum} className="bg-white dark:bg-dark-800 rounded-lg p-4 shadow-sm">
                                      <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                        ✅ Step {stepNum} Completion
                                      </h6>
                                      {formatDetailedDateTime(member[`step${stepNum}CompletionDate`])}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Summary Statistics */}
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-5 border border-purple-200 dark:border-purple-800">
                        <h6 className="font-bold text-lg text-purple-900 dark:text-purple-300 mb-4">
                          📊 Activity Summary
                        </h6>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-white dark:bg-dark-800 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-primary-600">
                              {selectedMenuUser?.isTaxFilingEnabled ? '✓' : '✗'}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Service Status</div>
                          </div>
                          <div className="bg-white dark:bg-dark-800 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-green-600">
                              {[1, 2, 3, 4].filter(s => selectedMenuUser?.[`step${s}CompletionDate`]).length}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Steps Completed</div>
                          </div>
                          <div className="bg-white dark:bg-dark-800 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-blue-600">
                              {selectedMenuUser?.members?.length || 0}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Total Members</div>
                          </div>
                          <div className="bg-white dark:bg-dark-800 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-orange-600">
                              {selectedMenuUser?.taxFilingLocked ? 'Locked' : 'Active'}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Filing Status</div>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* USA Formation Tab */}
            {activeTab === 'usa-formation' && (
              <div className="space-y-6">
                <div>
                  <h5 className="font-bold text-gray-900 dark:text-white mb-3">Company Formation Assignment</h5>
                  <div className="flex gap-2">
                    <select value={selectedMenuUser?.assignedCompanyForm || "LLC"} onChange={(e) => setSelectedMenuUser({ ...selectedMenuUser, assignedCompanyForm: e.target.value })} className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white">
                      <option value="LLC">LLC</option>
                      <option value="C-Corp">C-Corp</option>
                    </select>
                    <button onClick={() => handleCompanyFormAssignment(selectedMenuUser._id, selectedMenuUser.assignedCompanyForm)} className="px-4 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">Assign</button>
                  </div>
                </div>

                <div>
                  <h5 className="font-bold text-gray-900 dark:text-white mb-3">Submit USA Formation Steps</h5>
                  <select value={selectedStep} onChange={(e) => setSelectedStep(parseInt(e.target.value))} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white mb-2">
                    {[2, 3].map(step => {
                      const submitted = isUSAFormationStepSubmitted(selectedMenuUser?._id, step);
                      const canSub = canSubmitUSAFormationStep(selectedMenuUser?._id, step);
                      return <option key={step} value={step} disabled={submitted || !canSub}>Step {step}{submitted && " ✓"}{!submitted && !canSub && " (Locked)"}</option>;
                    })}
                  </select>
                  <button onClick={() => handleUSAFormationStepSubmit(selectedStep)} disabled={isUSAFormationStepSubmitted(selectedMenuUser?._id, selectedStep) || !canSubmitUSAFormationStep(selectedMenuUser?._id, selectedStep)} className="w-full px-4 py-2.5 bg-amber-500 text-white font-medium rounded-xl hover:bg-amber-600 disabled:opacity-50">{isUSAFormationStepSubmitted(selectedMenuUser?._id, selectedStep) ? "Already Submitted" : "Submit Step"}</button>
                  {isUSAFormationStepSubmitted(selectedMenuUser?._id, selectedStep) && <p className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-sm">This step has already been submitted.</p>}
                  {!canSubmitUSAFormationStep(selectedMenuUser?._id, selectedStep) && !isUSAFormationStepSubmitted(selectedMenuUser?._id, selectedStep) && <p className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 rounded-lg text-sm">Complete previous steps first.</p>}
                </div>
              </div>
            )}

            {/* Annual Report Tab */}
            {activeTab === 'annual-report' && (
              <div className="space-y-4">
                <h5 className="font-bold text-gray-900 dark:text-white">Annual Report Service - Due Date Setup</h5>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select State</label>
                  <select value={annualReportState || selectedMenuUser?.annualReportService?.state || ""} onChange={(e) => { setAnnualReportState(e.target.value); setShowEntityTypeSelect(e.target.value !== ""); setAnnualReportEntityType(""); setAnnualReportRegistrationDate(""); }} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white">
                    <option value="">-- Select State --</option>
                    <option value="Delaware">Delaware</option>
                    <option value="Wyoming">Wyoming</option>
                    <option value="New Jersey">New Jersey</option>
                    <option value="Florida">Florida</option>
                  </select>
                </div>

                {(showEntityTypeSelect || selectedMenuUser?.annualReportService?.state) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Entity Type</label>
                    <select value={annualReportEntityType || selectedMenuUser?.annualReportService?.entityType || ""} onChange={(e) => setAnnualReportEntityType(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white">
                      <option value="">-- Select Entity Type --</option>
                      <option value="LLC">LLC</option>
                      <option value="C-Corp">C-Corp</option>
                    </select>
                  </div>
                )}

                {(annualReportEntityType || selectedMenuUser?.annualReportService?.entityType) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Registration/Formation Date</label>
                    <input type="date" value={annualReportRegistrationDate || formatDateForInput(selectedMenuUser?.annualReportService?.registrationDate)} onChange={(e) => setAnnualReportRegistrationDate(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white" />
                    <p className="text-xs text-gray-500 mt-1">Enter the date when the company was registered/formed</p>
                  </div>
                )}

                {(annualReportState || selectedMenuUser?.annualReportService?.state) && (
                  <div className="p-3 bg-gray-100 dark:bg-dark-700 rounded-xl text-sm">
                    <strong className="text-gray-900 dark:text-white">Due Date Rules for {annualReportState || selectedMenuUser?.annualReportService?.state}:</strong>
                    <ul className="mt-2 space-y-1 text-gray-600 dark:text-gray-400 list-disc list-inside">
                      {(annualReportState || selectedMenuUser?.annualReportService?.state) === 'Delaware' && (<><li><strong>LLC:</strong> Due Date is June 1st of the following year</li><li><strong>C-Corp:</strong> Due Date is March 1st of the following year</li></>)}
                      {(annualReportState || selectedMenuUser?.annualReportService?.state) === 'Wyoming' && <li>Due Date is the 1st day of the anniversary month of the following year</li>}
                      {(annualReportState || selectedMenuUser?.annualReportService?.state) === 'New Jersey' && <li>Due Date is the last day of the anniversary month of the following year</li>}
                      {(annualReportState || selectedMenuUser?.annualReportService?.state) === 'Florida' && <li>Due between January 1 - May 1 each year. Final deadline: May 1st (by 11:59 PM)</li>}
                    </ul>
                  </div>
                )}

                <button onClick={() => { const s = annualReportState || selectedMenuUser?.annualReportService?.state; const e = annualReportEntityType || selectedMenuUser?.annualReportService?.entityType; const r = annualReportRegistrationDate || formatDateForInput(selectedMenuUser?.annualReportService?.registrationDate); if (s && e && r) { handleAnnualReportServiceUpdate(selectedMenuUser._id, s, e, r); setAnnualReportState(""); setAnnualReportEntityType(""); setAnnualReportRegistrationDate(""); setShowEntityTypeSelect(false); } }} disabled={!(annualReportState || selectedMenuUser?.annualReportService?.state) || !(annualReportEntityType || selectedMenuUser?.annualReportService?.entityType) || !(annualReportRegistrationDate || selectedMenuUser?.annualReportService?.registrationDate)} className="w-full px-4 py-2.5 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50">Save & Calculate Due Date</button>

                {selectedMenuUser?.annualReportService?.dueDate && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                    <h6 className="font-bold text-blue-800 dark:text-blue-300 mb-2">Current Annual Report Due Date</h6>
                    <p className="text-sm text-blue-700 dark:text-blue-400"><strong>State:</strong> {selectedMenuUser.annualReportService.state}</p>
                    <p className="text-sm text-blue-700 dark:text-blue-400"><strong>Entity Type:</strong> {selectedMenuUser.annualReportService.entityType}</p>
                    <p className="text-sm text-blue-700 dark:text-blue-400"><strong>Registration Date:</strong> {formatDateForDisplay(selectedMenuUser.annualReportService.registrationDate)}</p>
                    <div className="mt-2 p-2 bg-white dark:bg-dark-800 rounded-lg"><strong className="text-primary-600">Due Date:</strong> <span className="text-red-600 font-bold">{selectedMenuUser.annualReportService.dueDateText || formatDateForDisplay(selectedMenuUser.annualReportService.dueDate)}</span></div>
                  </div>
                )}
              </div>
            )}

            {/* Registered Agent Tab */}
            {activeTab === 'registered-agent' && (
              <div className="space-y-4">
                <h5 className="font-bold text-gray-900 dark:text-white">Registered Agent Service - Due Date Setup</h5>

                <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-xl">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Set the renewal due date for the Registered Agent service. Reminder emails will be sent automatically at:
                  </p>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside mb-4">
                    <li><strong>3 months</strong> before the due date</li>
                    <li><strong>1 month</strong> before the due date</li>
                    <li><strong>1 day</strong> before the due date</li>
                  </ul>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Renewal Due Date</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={registeredAgentDueDate || formatDateForInput(selectedMenuUser?.registeredAgentService?.dueDate)}
                      onChange={(e) => setRegisteredAgentDueDate(e.target.value)}
                      className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white"
                    />
                    <button
                      onClick={() => {
                        const d = registeredAgentDueDate || formatDateForInput(selectedMenuUser?.registeredAgentService?.dueDate);
                        if (d) {
                          handleRegisteredAgentDueDateUpdate(selectedMenuUser._id, d);
                          setRegisteredAgentDueDate("");
                        }
                      }}
                      disabled={!registeredAgentDueDate && !selectedMenuUser?.registeredAgentService?.dueDate}
                      className="px-4 py-2.5 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50"
                    >
                      Update
                    </button>
                  </div>
                </div>

                {selectedMenuUser?.registeredAgentService?.dueDate && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                    <h6 className="font-bold text-blue-800 dark:text-blue-300 mb-2">Current Registered Agent Due Date</h6>
                    <div className="mt-2 p-3 bg-white dark:bg-dark-800 rounded-lg">
                      <strong className="text-primary-600">Renewal Due Date:</strong>
                      <span className="text-red-600 font-bold ml-2">{formatDateForDisplay(selectedMenuUser.registeredAgentService.dueDate)}</span>
                    </div>
                    <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                      <p><strong>Reminder Status:</strong></p>
                      <ul className="mt-1 space-y-1">
                        <li>• 3-Month Reminder: {selectedMenuUser.registeredAgentService.threeMonthReminderSent ? <span className="text-green-600">✓ Sent</span> : <span className="text-yellow-600">Pending</span>}</li>
                        <li>• 1-Month Reminder: {selectedMenuUser.registeredAgentService.oneMonthReminderSent ? <span className="text-green-600">✓ Sent</span> : <span className="text-yellow-600">Pending</span>}</li>
                        <li>• 1-Day Reminder: {selectedMenuUser.registeredAgentService.oneDayReminderSent ? <span className="text-green-600">✓ Sent</span> : <span className="text-yellow-600">Pending</span>}</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Signature Box Editor Modal */}
          <SignatureBoxEditor
            isOpen={step4EditorOpen}
            onClose={() => setStep4EditorOpen(false)}
            documentUrl={step4EditorDocUrl}
            existingBoxes={step4EditorBoxes}
            onSave={handleStep4SaveBoxes}
            isSaving={step4SavingBoxes}
          />

          {/* Move to History Confirmation Modal */}
          {showMoveToHistoryConfirm && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-10">
              <div className="bg-white dark:bg-dark-800 rounded-xl p-6 max-w-md w-full shadow-2xl">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FaHistory className="text-purple-500" /> Confirm Move to History
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  This action will:
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 mb-4 list-disc list-inside space-y-1">
                  <li>Save current tax filing cycle to history</li>
                  <li>Reset all steps for a new cycle</li>
                  <li>Lock the new cycle (user must pay to unlock)</li>
                  {newCycleTaxYearEndDate && <li>Set new Tax Year End: {formatDateForDisplay(newCycleTaxYearEndDate)}</li>}
                  {newCycleDueDate && <li>Set new Due Date: {formatDateForDisplay(newCycleDueDate)}</li>}
                </ul>
                <div className="flex gap-3">
                  <button onClick={() => { setShowMoveToHistoryConfirm(false); setMoveToHistoryMemberIndex(null); }} className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-dark-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-300 dark:hover:bg-dark-500">
                    Cancel
                  </button>
                  <button onClick={confirmMoveToHistory} className="flex-1 px-4 py-2.5 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 flex items-center justify-center gap-2">
                    <FaCheck /> Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientMenuModal;
